import type { DataType } from '@numistic/contracts'
import { useRefList } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button, Checkbox, Input, Select, Textarea } from '@numistic/ui'
import { useState } from 'react'

import type { Widget, WidgetProps } from '../types.js'
import { RefCombobox } from './ref-combobox.js'
import { selectable, useRefOptions } from './ref-options.js'

const str = (v: unknown) => (v === undefined || v === null ? '' : String(v))
/** Пустой ввод — '' (см. WidgetProps.onChange). */
const text = (v: string) => v

function TextWidget({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
  field,
}: WidgetProps<string>) {
  return (
    <Input
      id={id}
      value={str(value)}
      maxLength={field.def.validation.maxLength}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value))}
      onBlur={onBlur}
    />
  )
}

function LongTextWidget({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
}: WidgetProps<string>) {
  return (
    <Textarea
      id={id}
      rows={textRows(str(value))}
      className="max-h-[32rem] field-sizing-content"
      value={str(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value))}
      onBlur={onBlur}
    />
  )
}

/** Высота по тексту (запасной путь, где нет field-sizing): 3–14 строк, ~80 знаков в строке. */
function textRows(value: string): number {
  const lines = value
    .split('\n')
    .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 80)), 0)
  return Math.min(14, Math.max(3, lines))
}

function NumberWidget({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
  field,
}: WidgetProps<string | number>) {
  const integer = field.def.dataType === 'integer'
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        inputMode={integer ? 'numeric' : 'decimal'}
        value={str(value)}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(text(e.target.value))}
        onBlur={onBlur}
      />
      {field.def.unit ? (
        <span className="text-sm text-muted-foreground">{field.def.unit}</span>
      ) : null}
    </div>
  )
}

function BooleanWidget({ id, value, onChange, invalid, describedBy }: WidgetProps<boolean>) {
  const { t } = useTranslation()
  return (
    <Select
      id={id}
      value={value === undefined || value === null ? '' : String(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
    >
      <option value="">{t('common.notSet')}</option>
      <option value="true">{t('common.yes')}</option>
      <option value="false">{t('common.no')}</option>
    </Select>
  )
}

function DateWidget({ id, value, onChange, onBlur, invalid, describedBy }: WidgetProps<string>) {
  return (
    <Input
      id={id}
      type="date"
      value={str(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value))}
      onBlur={onBlur}
    />
  )
}

function PartialDateWidget({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
}: WidgetProps<string>) {
  const { t } = useTranslation()
  return (
    <Input
      id={id}
      placeholder={t('meta.partialDateHint')}
      value={str(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value.trim()))}
      onBlur={onBlur}
    />
  )
}

type YearRange = { from?: string | number; to?: string | number | null }

function YearRangeWidget({
  id,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
}: WidgetProps<YearRange>) {
  const { t } = useTranslation()
  const update = (patch: YearRange) => {
    const next = { from: str(value?.from), to: str(value?.to), ...patch }
    onChange(str(next.from) === '' && str(next.to) === '' ? null : next)
  }
  return (
    <div className="flex items-center gap-2" role="group" aria-describedby={describedBy}>
      <span className="text-sm text-muted-foreground">{t('meta.yearFrom')}</span>
      <Input
        id={id}
        inputMode="numeric"
        className="w-24"
        value={str(value?.from)}
        aria-invalid={invalid || undefined}
        onChange={(e) => update({ from: e.target.value })}
        onBlur={onBlur}
      />
      <span className="text-sm text-muted-foreground">{t('meta.yearTo')}</span>
      <Input
        inputMode="numeric"
        className="w-24"
        aria-label={t('meta.yearTo')}
        value={str(value?.to)}
        onChange={(e) => update({ to: e.target.value })}
        onBlur={onBlur}
      />
    </div>
  )
}

function EnumWidget({
  id,
  meta,
  field,
  value,
  onChange,
  invalid,
  describedBy,
}: WidgetProps<string>) {
  const { t } = useTranslation()
  const options = selectable(meta.enums[field.def.enumSet ?? ''] ?? [], (o) => o.code === value)
  return (
    <Select
      id={id}
      value={str(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value))}
    >
      <option value="">{t('common.notSet')}</option>
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.name.ru}
        </option>
      ))}
    </Select>
  )
}

function MultiEnumWidget({ id, meta, field, value, onChange, describedBy }: WidgetProps<string[]>) {
  const selected = new Set(value ?? [])
  const options = selectable(meta.enums[field.def.enumSet ?? ''] ?? [], (o) => selected.has(o.code))
  const toggle = (code: string) => {
    const next = options
      .map((o) => o.code)
      .filter((c) => (c === code ? !selected.has(c) : selected.has(c)))
    onChange(next)
  }
  return (
    <div
      id={id}
      role="group"
      aria-describedby={describedBy}
      className="grid gap-1.5 sm:grid-cols-2"
    >
      {options.map((o) => (
        <label key={o.code} className="flex items-center gap-2 text-sm">
          <Checkbox checked={selected.has(o.code)} onChange={() => toggle(o.code)} />
          {o.name.ru}
        </label>
      ))}
    </div>
  )
}

function RefWidget({
  id,
  meta,
  field,
  value,
  onChange,
  invalid,
  describedBy,
}: WidgetProps<string>) {
  const { t } = useTranslation()
  const refOptions = useRefOptions(field.def.refKind, meta)
  const { available, loading } = refOptions
  const options = refOptions.options.filter((o) => !o.inactive || o.id === value)
  // Длинные справочники (эмитенты — сотни записей) — с поиском.
  if (available && options.length > 30)
    return (
      <RefCombobox
        id={id}
        options={options}
        value={value}
        onChange={onChange}
        invalid={invalid}
        describedBy={describedBy}
      />
    )
  return (
    <Select
      id={id}
      value={str(value)}
      disabled={!available || loading}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value))}
    >
      <option value="">
        {loading ? t('common.loading') : available ? t('common.notSet') : t('meta.refUnavailable')}
      </option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </Select>
  )
}

/** Популярные валюты — в начале списка. */
const TOP_CURRENCIES = ['RUB', 'USD', 'EUR']

function MoneyWidget({
  id,
  meta,
  value,
  onChange,
  onBlur,
  invalid,
  describedBy,
}: WidgetProps<{ amount?: string; currency?: string }>) {
  const { t } = useTranslation()
  const currencies = [
    ...TOP_CURRENCIES.flatMap((code) => meta.currencies.filter((c) => c.code === code)),
    ...meta.currencies.filter((c) => !TOP_CURRENCIES.includes(c.code)),
  ]
  // Пока суммы нет, значение поля — null: выбранную валюту помним здесь, иначе она «отскакивает».
  const [draftCurrency, setDraftCurrency] = useState<string>()
  const currency = value?.currency ?? draftCurrency ?? 'RUB'
  const update = (patch: { amount?: string; currency?: string }) => {
    if (patch.currency) setDraftCurrency(patch.currency)
    const next = { amount: str(value?.amount), currency, ...patch }
    onChange(next.amount === '' ? null : next)
  }
  return (
    <div className="flex gap-2" role="group" aria-describedby={describedBy}>
      <Input
        id={id}
        inputMode="decimal"
        placeholder={t('meta.amount')}
        value={str(value?.amount)}
        aria-invalid={invalid || undefined}
        onChange={(e) => update({ amount: e.target.value })}
        onBlur={onBlur}
      />
      <Select
        className="w-28"
        aria-label={t('meta.currency')}
        value={currency}
        onChange={(e) => update({ currency: e.target.value })}
      >
        {currencies.map((c) => (
          <option key={c.code} value={c.code} title={c.name.ru}>
            {c.code}
          </option>
        ))}
      </Select>
    </div>
  )
}

function GradeWidget({
  id,
  meta,
  value,
  onChange,
  invalid,
  describedBy,
  typeCode,
}: WidgetProps<{ scale?: string; value?: string }>) {
  const { t } = useTranslation()
  const defaultScale =
    meta.itemTypes.find((x) => x.code === typeCode)?.defaultGradeScale ??
    meta.gradeScales[0]?.code ??
    ''
  // Шкалу можно выбрать до значения, поэтому она живёт в локальном состоянии.
  const [scaleCode, setScaleCode] = useState(value?.scale ?? defaultScale)
  const scale = meta.gradeScales.find((s) => s.code === scaleCode)
  return (
    <div className="flex gap-2" role="group" aria-describedby={describedBy}>
      <Select
        className="w-44"
        aria-label={t('meta.scale')}
        value={scaleCode}
        onChange={(e) => {
          setScaleCode(e.target.value)
          onChange(null)
        }}
      >
        {selectable(meta.gradeScales, (s) => s.code === scaleCode).map((s) => (
          <option key={s.code} value={s.code}>
            {s.name.ru}
          </option>
        ))}
      </Select>
      <Select
        id={id}
        aria-invalid={invalid || undefined}
        value={value?.scale === scaleCode ? str(value?.value) : ''}
        onChange={(e) =>
          onChange(e.target.value ? { scale: scaleCode, value: e.target.value } : null)
        }
      >
        <option value="">{t('common.notSet')}</option>
        {selectable(
          scale?.values ?? [],
          (v) => value?.scale === scaleCode && v.code === value.value,
        ).map((v) => (
          <option key={v.code} value={v.code}>
            {v.name.ru}
          </option>
        ))}
      </Select>
    </div>
  )
}

type CatalogNumber = { system?: string; number?: string }

function ListWidget({
  id,
  field,
  value,
  onChange,
  describedBy,
  typeCode,
}: WidgetProps<Array<string | CatalogNumber>>) {
  const { t } = useTranslation()
  const catalogNumbers = field.def.listOf === 'catalog_number'
  const systems = useRefList('numbering_systems', { enabled: catalogNumbers })
  const items = value ?? []
  const emit = (next: Array<string | CatalogNumber>) => onChange(next)
  const setItem = (index: number, item: string | CatalogNumber) =>
    emit(items.map((x, i) => (i === index ? item : x)))
  const applicable = (systems.data ?? []).filter(
    (s) => s.isActive && (s.appliesTo.length === 0 || !typeCode || s.appliesTo.includes(typeCode)),
  )
  return (
    <div className="grid gap-2" role="group" aria-describedby={describedBy}>
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          {catalogNumbers ? (
            <>
              <Select
                className="w-44"
                aria-label={t('meta.system')}
                value={(item as CatalogNumber).system ?? ''}
                onChange={(e) =>
                  setItem(index, { ...(item as CatalogNumber), system: e.target.value })
                }
              >
                <option value="">{t('meta.choose')}</option>
                {applicable.map((s) => (
                  <option key={s.id} value={s.code}>
                    {s.name.ru}
                  </option>
                ))}
              </Select>
              <Input
                id={index === 0 ? id : undefined}
                aria-label={t('meta.number')}
                value={(item as CatalogNumber).number ?? ''}
                onChange={(e) =>
                  setItem(index, { ...(item as CatalogNumber), number: e.target.value })
                }
              />
            </>
          ) : (
            <Input
              id={index === 0 ? id : undefined}
              type={field.def.listOf === 'url' ? 'url' : 'text'}
              value={str(item)}
              onChange={(e) => setItem(index, e.target.value)}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => emit(items.filter((_, i) => i !== index))}
          >
            {t('common.remove')}
          </Button>
        </div>
      ))}
      <Button
        id={items.length === 0 ? id : undefined}
        type="button"
        variant="outline"
        size="sm"
        className="justify-self-start"
        onClick={() =>
          emit([...items, catalogNumbers ? { system: applicable[0]?.code ?? '', number: '' } : ''])
        }
      >
        {t('meta.addItem')}
      </Button>
    </div>
  )
}

function UrlWidget({ id, value, onChange, onBlur, invalid, describedBy }: WidgetProps<string>) {
  return (
    <Input
      id={id}
      type="url"
      value={str(value)}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(text(e.target.value.trim()))}
      onBlur={onBlur}
    />
  )
}

/**
 * Реестр виджетов — единственное место UI, которое знает о типах данных (docs/PLAN.md, раздел 6.2).
 * Record<DataType, …>: новый тип данных без виджета не скомпилируется.
 */
export const widgets: Record<DataType, Widget> = {
  text: TextWidget,
  longtext: LongTextWidget,
  integer: NumberWidget,
  decimal: NumberWidget,
  boolean: BooleanWidget,
  date: DateWidget,
  partial_date: PartialDateWidget,
  year_range: YearRangeWidget,
  enum: EnumWidget,
  multi_enum: MultiEnumWidget,
  ref: RefWidget,
  money: MoneyWidget,
  grade: GradeWidget,
  list: ListWidget,
  url: UrlWidget,
}
